import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AutoComplete } from 'primereact/autocomplete';
import { useSpecs } from '../hooks/useSpecs';

interface EndpointExampleSelectorProps {
  specId: number | undefined;
  endpointPath: string;
  endpointMethod: string;
  exampleName: string;
  onSpecChange: (specId: number | undefined) => void;
  onEndpointChange: (path: string, method: string) => void;
  onExampleChange: (name: string) => void;
}

const EndpointExampleSelector: React.FC<EndpointExampleSelectorProps> = ({
  specId,
  endpointPath,
  endpointMethod,
  exampleName,
  onSpecChange,
  onEndpointChange,
  onExampleChange,
}) => {
  const { specs, endpoints, loadingSpecs, loadingEndpoints, loadEndpoints } = useSpecs();
  const [endpointQuery, setEndpointQuery] = useState('');
  const [filteredEndpoints, setFilteredEndpoints] = useState<string[]>([]);

  useEffect(() => {
    if (specId) {
      loadEndpoints(specId);
    }
  }, [specId, loadEndpoints]);

  useEffect(() => {
    if (endpointPath && endpointMethod) {
      setEndpointQuery(`${endpointMethod.toUpperCase()} ${endpointPath}`);
    } else {
      setEndpointQuery('');
    }
  }, [endpointPath, endpointMethod]);

  const normalizedEndpointMethod = endpointMethod ? endpointMethod.toLowerCase() : '';
  const selectedEndpoint = endpoints.find(e => e.path === endpointPath && e.method === normalizedEndpointMethod);
  const endpointOptions = useMemo(() => endpoints.map(ep => `${ep.method.toUpperCase()} ${ep.path}`), [endpoints]);
  const endpointLabelMap = useMemo(() => {
    const map = new Map<string, { path: string; method: string; description?: string }>();
    endpoints.forEach(ep => {
      map.set(`${ep.method.toUpperCase()} ${ep.path}`, {
        path: ep.path,
        method: ep.method,
        description: ep.description,
      });
    });
    return map;
  }, [endpoints]);

  const filterEndpoints = useCallback(
    (query: string) => {
      const lower = query.toLowerCase();
      const results = endpointOptions.filter(option => {
        if (option.toLowerCase().includes(lower)) return true;
        const meta = endpointLabelMap.get(option);
        const description = meta?.description ? meta.description.toLowerCase() : '';
        return description.includes(lower);
      });
      setFilteredEndpoints(results);
    },
    [endpointOptions, endpointLabelMap]
  );

  useEffect(() => {
    setFilteredEndpoints(endpointOptions);
  }, [endpointOptions]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <div>
        <label style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '4px' }}>
          Linked Spec
        </label>
        <select
          value={specId || ''}
          onChange={e => {
            const val = e.target.value ? Number(e.target.value) : undefined;
            onSpecChange(val);
            onEndpointChange('', '');
            onExampleChange('');
          }}
          style={{ width: '100%', padding: '6px', fontSize: '13px', borderRadius: '4px', border: '1px solid #ddd' }}
        >
          <option value="">-- None --</option>
          {specs.map(s => (
            <option key={s.id} value={s.id}>
              {s.title || s.name}
            </option>
          ))}
        </select>
        {loadingSpecs && <span style={{ fontSize: '11px', color: '#999' }}>Loading...</span>}
      </div>

      {specId && (
        <div>
          <label style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '4px' }}>
            Endpoint
          </label>
          <AutoComplete
            value={endpointQuery}
            suggestions={filteredEndpoints}
            completeMethod={e => filterEndpoints(e.query)}
            onChange={e => {
              const value = e.value || '';
              setEndpointQuery(value);
              if (!value) {
                onEndpointChange('', '');
                onExampleChange('');
              }
            }}
            onSelect={e => {
              const value = e.value as string;
              const match = endpointLabelMap.get(value);
              if (match) {
                onEndpointChange(match.path, match.method);
                onExampleChange('');
              }
            }}
            onBlur={() => {
              const match = endpointLabelMap.get(endpointQuery);
              if (match) {
                onEndpointChange(match.path, match.method);
              }
            }}
            dropdown
            placeholder={
              loadingEndpoints
                ? 'Loading endpoints...'
                : endpoints.length > 0
                  ? 'Search endpoint...'
                  : 'No endpoints found'
            }
            style={{ width: '100%' }}
            inputStyle={{ width: '100%', fontSize: '13px' }}
            itemTemplate={(option: string) => {
              const meta = endpointLabelMap.get(option);
              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  <span style={{ fontSize: '13px', fontWeight: 600, color: '#111' }}>{option}</span>
                  {meta?.description && (
                    <span style={{ fontSize: '11px', color: '#64748b' }}>{meta.description}</span>
                  )}
                </div>
              );
            }}
          />
          {loadingEndpoints && <span style={{ fontSize: '11px', color: '#999' }}>Loading endpoints...</span>}
          {!loadingEndpoints && endpoints.length === 0 && (
            <span style={{ fontSize: '11px', color: '#999' }}>No endpoints available for this spec.</span>
          )}
          {selectedEndpoint?.description && (
            <div style={{ marginTop: '6px', fontSize: '11px', color: '#475569' }}>
              Description: {selectedEndpoint.description}
            </div>
          )}
        </div>
      )}

      {selectedEndpoint && selectedEndpoint.examples.length > 0 && (
        <div>
          <label style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '4px' }}>
            Example
          </label>
          <select
            value={exampleName || ''}
            onChange={e => onExampleChange(e.target.value)}
            style={{ width: '100%', padding: '6px', fontSize: '13px', borderRadius: '4px', border: '1px solid #ddd' }}
          >
            <option value="">-- Select Example --</option>
            {selectedEndpoint.examples.map(ex => (
              <option key={ex} value={ex}>
                {ex}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
};

export default EndpointExampleSelector;
